import { context, trace } from "@opentelemetry/api";
import type { Logger } from "@opentelemetry/api-logs";
import { logs, SeverityNumber } from "@opentelemetry/api-logs";
import { Resource } from "@opentelemetry/resources";
import {
  BatchLogRecordProcessor,
  type BatchLogRecordProcessorBrowserConfig,
  LoggerProvider as SDKLoggerProvider,
} from "@opentelemetry/sdk-logs";
import { ClientJS } from "clientjs";

import { PACKAGE_VERSION } from "../constants";
import CONV from "../semantic-conventions";
import type { UserInfo } from "../sessions/types";
import { HeadersInBodyOTLPLogExporter } from "./HeadersInBodyOTLPLogExporter";

const LOGGER_INSTRUMENTATION_NAME = "last9-browser-sdk-logger";
const LOGGER_INSTRUMENTATION_VERSION = PACKAGE_VERSION;
const DEFAULT_BATCH_CONFIG: BatchLogRecordProcessorBrowserConfig = {
  maxExportBatchSize: 1024,
  maxQueueSize: 2048,
  scheduledDelayMillis: 5000,
  exportTimeoutMillis: 10000,
};

type LogLevel = "info" | "warn" | "error";

type PendingLog = {
  level: LogLevel;
  args: unknown[];
};

const severityMapping: Record<LogLevel, { number: SeverityNumber; text: string }> = {
  info: { number: SeverityNumber.INFO, text: "INFO" },
  warn: { number: SeverityNumber.WARN2, text: "WARN2" },
  error: { number: SeverityNumber.ERROR3, text: "ERROR3" },
};

let LOGS_ENABLED: boolean = false; // Remote log emission toggle (disabled by default)
let loggerProvider: SDKLoggerProvider | undefined;
let loggerProcessor: BatchLogRecordProcessor | undefined;
let loggerInstance: Logger | undefined;
let loggerExporter: HeadersInBodyOTLPLogExporter | undefined;
let loggerBaseAttributes: Record<string, string> = {};
let loggerDynamicAttributes: Record<string, string> = {};
let cachedClientId: string | undefined;
let configuredResourceAttributes: Record<string, unknown> | undefined;
const pendingLogQueue: PendingLog[] = [];
const MAX_PENDING_LOGS = 50;
let configuredToken: string | undefined;
let configuredBaseUrl: string | undefined;
let configuredServiceName: string | undefined;

const isBrowserEnvironment = () =>
  typeof window !== "undefined" &&
  typeof navigator !== "undefined" &&
  typeof document !== "undefined";

const getOrigin = (): string | undefined => {
  return window.location.origin;
};

const getClientId = (): string | undefined => {
  if (!isBrowserEnvironment()) return undefined;
  try {
    const client = new ClientJS();
    return client.getFingerprint().toString();
  } catch {
    return undefined;
  }
};

const resolveLogToken = (): string | undefined => {
  // token parameter (from SDK initialization)
  if (configuredToken) {
    const trimmed = configuredToken.trim();
    if (trimmed) return trimmed;
  }

  return undefined;
};

const formatAuthorizationHeader = (token: string): string => `Bearer ${token}`;

const ensureLogEndpoint = (candidate: string): string => {
  try {
    const url = new URL(candidate);
    const path = url.pathname.replace(/\/+$/, "");
    if (!path || path === "") {
      url.pathname = "/v1/logs";
    }
    return url.toString();
  } catch {
    throw new Error(`L9RUM: Invalid log endpoint URL "${candidate}"`);
  }
};

const normalizeBuildType = (): string => {
  const fromNodeEnv =
    typeof globalThis !== "undefined" && (globalThis as any)?.process?.env?.NODE_ENV;

  const candidate = (fromNodeEnv || "").toString().toLowerCase();
  if (!candidate) {
    return "unknown";
  }
  if (["prod", "production", "live"].includes(candidate)) {
    return "prod";
  }
  if (
    ["alpha", "staging", "stage", "beta", "dev", "development", "qa", "test"].includes(candidate)
  ) {
    return "alpha";
  }
  return candidate;
};

const safeSerialize = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (value instanceof Error) {
    return value.stack || `${value.name}: ${value.message}`;
  }
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null ||
    value === undefined
  ) {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
};

const filterStringEntries = (entries: [string, string | null | undefined][]): [string, string][] =>
  entries.filter((entry): entry is [string, string] => {
    const value = entry[1];
    return value !== undefined && value !== null && value !== "";
  });

type StructuredLogInput = {
  message: string;
  attributes?: Record<string, unknown>;
  error?: unknown;
  severityNumber?: SeverityNumber;
  severityText?: string;
};

type ParsedLog = {
  message: string;
  severityNumber: SeverityNumber;
  severityText: string;
  attributes: Record<string, string>;
  error?: Error;
  isStructured?: boolean;
  rawArguments?: string[];
};

const isStructuredLogInput = (value: unknown): value is StructuredLogInput => {
  return !!value && typeof value === "object" && typeof (value as any).message === "string";
};

const toStringRecord = (attributes?: Record<string, unknown>): Record<string, string> => {
  if (!attributes) return {};
  const output: Record<string, string> = {};
  for (const [key, val] of Object.entries(attributes)) {
    if (val === undefined || val === null) continue;
    const serialized = safeSerialize(val);
    if (serialized !== "") {
      output[key] = serialized;
    }
  }
  return output;
};

const resolveError = (input: unknown): Error | undefined => {
  if (!input) return undefined;
  if (input instanceof Error) return input;
  if (typeof input === "string") {
    return new Error(input);
  }
  try {
    return new Error(safeSerialize(input));
  } catch {
    return new Error(String(input));
  }
};

const resolveSeverityText = (number: SeverityNumber, fallback: string): string => {
  const derived = (SeverityNumber as any)[number];
  if (typeof derived === "string" && derived.trim()) {
    return derived;
  }
  return fallback;
};

const buildResourceMap = (): Record<string, string> => {
  const entries: [string, string | null | undefined][] = [];

  const buildType = normalizeBuildType();
  const resolvedServiceName = (configuredServiceName && configuredServiceName.trim()) || "L9 RUM";

  entries.push(
    ["service.name", resolvedServiceName],
    ["build.type", buildType],
    ["l9.sdk.version", PACKAGE_VERSION],
  );

  if (configuredResourceAttributes) {
    for (const [key, value] of Object.entries(configuredResourceAttributes)) {
      if (value === undefined || value === null) continue;
      const serialized = safeSerialize(value);
      if (serialized !== "") {
        entries.push([key, serialized]);
      }
    }
  }

  const resourceAttributeEntries = filterStringEntries(entries);
  const resourceMap: Record<string, string> = {};
  for (const [key, value] of resourceAttributeEntries) {
    resourceMap[key] = value;
  }
  return resourceMap;
};

const parseLogArguments = (level: LogLevel, args: unknown[]): ParsedLog => {
  const baseSeverity = severityMapping[level];
  if (args.length === 1 && isStructuredLogInput(args[0])) {
    const structured = args[0];
    const severityNumber = structured.severityNumber ?? baseSeverity.number;
    const severityText = structured.severityText
      ? structured.severityText
      : resolveSeverityText(severityNumber, baseSeverity.text);
    return {
      message: structured.message,
      severityNumber,
      severityText,
      attributes: toStringRecord(structured.attributes),
      error: resolveError(structured.error),
      isStructured: true,
    };
  }

  const serializedArgs = args.map(safeSerialize);
  const message = serializedArgs.length ? serializedArgs.join(" ") : level;
  const errorArg = args.find((arg) => arg instanceof Error) as Error | undefined;
  return {
    message,
    severityNumber: baseSeverity.number,
    severityText: baseSeverity.text,
    attributes: {},
    error: errorArg,
    isStructured: false,
    rawArguments: serializedArgs,
  };
};

const enqueuePendingLog = (level: LogLevel, args: unknown[]): void => {
  if (pendingLogQueue.length >= MAX_PENDING_LOGS) {
    pendingLogQueue.shift();
  }
  pendingLogQueue.push({ level, args });
};

const flushPendingLogs = (): void => {
  if (!loggerInstance || pendingLogQueue.length === 0) return;
  const queueCopy = pendingLogQueue.splice(0, pendingLogQueue.length);
  for (const entry of queueCopy) {
    sendToLogger(entry.level, entry.args);
  }
};

const setDynamicAttr = (key: string, value: string | undefined) => {
  if (value === undefined || value === null || value === "") {
    delete loggerDynamicAttributes[key];
    return;
  }
  loggerDynamicAttributes[key] = value;
};

export const updateLoggerSessionContext = (sessionId?: string, previousId?: string): void => {
  setDynamicAttr(CONV.SESSION_ID, sessionId);
  setDynamicAttr(CONV.SESSION_PREVIOUS_ID, previousId);
};

export const updateLoggerUserContext = (user?: UserInfo): void => {
  if (!user) {
    setDynamicAttr(CONV.USER_ID, undefined);
    setDynamicAttr(CONV.USER_NAME, undefined);
    setDynamicAttr(CONV.USER_FULL_NAME, undefined);
    setDynamicAttr(CONV.USER_EMAIL, undefined);
    setDynamicAttr(CONV.USER_ROLES, undefined);
    return;
  }

  setDynamicAttr(CONV.USER_ID, user.id);
  setDynamicAttr(CONV.USER_NAME, user.name);
  setDynamicAttr(CONV.USER_FULL_NAME, user.fullName);
  setDynamicAttr(CONV.USER_EMAIL, user.email);
  setDynamicAttr(
    CONV.USER_ROLES,
    user.roles && user.roles.length ? JSON.stringify(user.roles) : undefined,
  );
};

export const updateLoggerViewId = (viewId?: string): void => {
  setDynamicAttr("view.id", viewId);
};

const getPageAttributes = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  try {
    const { location } = window;
    return {
      origin: location.origin,
      path: location.pathname,
      [CONV.PAGE_URL]: location.href,
      [CONV.PAGE_HASH]: location.hash,
      [CONV.PAGE_ROUTE]: location.pathname,
      [CONV.PAGE_HOSTNAME]: location.hostname,
      [CONV.PAGE_SEARCH]: location.search,
      [CONV.URL_PATH]: location.pathname,
    };
  } catch {
    return {};
  }
};

const disableExistingLogger = () => {
  loggerBaseAttributes = {};
  loggerInstance = undefined;
  if (loggerProvider) {
    void loggerProvider.shutdown();
    loggerProvider = undefined;
  }
  if (loggerExporter) {
    void loggerExporter.shutdown();
    loggerExporter = undefined;
  }
  loggerProcessor = undefined;
  logs.disable();
  pendingLogQueue.splice(0, pendingLogQueue.length);
};

export const configureLogger = (
  newToken: string,
  newBaseUrl: string,
  serviceName?: string,
  resourceAttributes?: Record<string, unknown>,
): void => {
  // Store token and baseUrl for use during configuration
  configuredToken = newToken;
  configuredBaseUrl = newBaseUrl;
  configuredServiceName = serviceName;
  configuredResourceAttributes = resourceAttributes;

  if (!isBrowserEnvironment()) {
    warn({
      message: "[L9RUM Browser SDK] Logger disabled outside browser context",
      attributes: {
        "log.guard": "no_browser",
        "log.phase": "init",
      },
    });
    disableExistingLogger();
    return;
  }

  const token = resolveLogToken();

  if (!token) {
    warn({
      message: "[L9RUM Browser SDK] Logger disabled due to missing token",
      attributes: {
        "log.guard": "missing_token",
        "log.phase": "init",
      },
    });
    disableExistingLogger();
    return;
  }

  const origin = getOrigin();
  cachedClientId = getClientId();

  const resourceMap = buildResourceMap();

  const baseAttributes: Record<string, string> = { ...resourceMap };

  if (origin) {
    baseAttributes["browser.origin"] = origin;
  }
  if (cachedClientId) {
    baseAttributes["client.id"] = cachedClientId;
  }

  try {
    const headersMap: Record<string, string> = {
      "X-LAST9-API-TOKEN": formatAuthorizationHeader(token),
    };

    if (origin) {
      headersMap["X-LAST9-ORIGIN"] = origin;
    }

    if (cachedClientId) {
      headersMap["Client-ID"] = cachedClientId;
    }

    // Construct log endpoint from baseUrl if provided, otherwise use default
    const logEndpoint = (() => {
      if (configuredBaseUrl && typeof configuredBaseUrl === "string" && configuredBaseUrl.trim()) {
        const trimmed = configuredBaseUrl.replace(/\/+$/, "");
        return `${trimmed}/telemetry/beacon/v1/logs`;
      }
    })();

    const exporter = new HeadersInBodyOTLPLogExporter(
      {
        url: ensureLogEndpoint(logEndpoint ?? ""),
        timeoutMillis: 10000,
      },
      headersMap,
    );

    loggerProvider = new SDKLoggerProvider({
      resource: new Resource(resourceMap),
      mergeResourceWithDefaults: true,
    });
    loggerProcessor = new BatchLogRecordProcessor(exporter, DEFAULT_BATCH_CONFIG);
    loggerProvider.addLogRecordProcessor(loggerProcessor);
    logs.setGlobalLoggerProvider(loggerProvider);
    loggerInstance = logs.getLogger(LOGGER_INSTRUMENTATION_NAME, LOGGER_INSTRUMENTATION_VERSION);
    loggerBaseAttributes = baseAttributes;
    loggerExporter = exporter;
    flushPendingLogs();
    log({
      message: "[L9RUM Browser SDK] Logger configured",
      attributes: {
        "log.phase": "init",
        "log.guard": "configured",
      },
    });
  } catch (err) {
    error({
      message: "[L9RUM Browser SDK] Failed to initialize log exporter",
      attributes: {
        "log.phase": "init",
        "log.guard": "exporter_init_failed",
      },
      error: err,
    });
  }
};

const emitLogRecord = (
  level: LogLevel,
  args: unknown[],
  bypassLogsEnabled: boolean = false,
): void => {
  // Check if logger is initialized (required)
  if (!loggerInstance) {
    if (!bypassLogsEnabled) {
      enqueuePendingLog(level, args);
    }
    return;
  }

  // Check LOGS_ENABLED unless bypassing
  if (!bypassLogsEnabled && !LOGS_ENABLED) {
    enqueuePendingLog(level, args);
    return;
  }

  try {
    const parsed = parseLogArguments(level, args);
    const active = context.active();
    const activeSpan = trace.getActiveSpan();
    const ctxAttrs: Record<string, string> = {};
    if (activeSpan && typeof activeSpan.spanContext === "function") {
      const sc = activeSpan.spanContext();
      if (sc?.traceId) ctxAttrs["trace.id"] = sc.traceId;
      if (sc?.spanId) ctxAttrs["span.id"] = sc.spanId;
    }

    const attributes: Record<string, string> = {
      ...loggerBaseAttributes,
      ...loggerDynamicAttributes,
      ...getPageAttributes(),
      ...ctxAttrs,
      ...parsed.attributes,
      "log.level": level,
    };

    if (parsed.isStructured) {
      attributes["log.structured"] = "true";
    } else if (parsed.rawArguments && parsed.rawArguments.length > 1) {
      attributes["log.arguments"] = JSON.stringify(parsed.rawArguments);
    }

    const errorArg = parsed.error;
    if (errorArg) {
      attributes["error.message"] = errorArg.message || "";
      if (errorArg.name) {
        attributes["error.type"] = errorArg.name;
      }
      if (errorArg.stack) {
        attributes["error.stack"] = errorArg.stack;
      }
    }

    // Ensure error events are always logged with error severity
    let severityNumber = parsed.severityNumber;
    let severityText = parsed.severityText;
    if (attributes["event.type"] === "error") {
      severityNumber = SeverityNumber.ERROR;
      severityText = resolveSeverityText(severityNumber, "ERROR3");
    }

    loggerInstance.emit({
      timestamp: Date.now(),
      severityNumber,
      severityText,
      body: parsed.message,
      attributes,
      context: active,
    });
  } catch {
    // Swallow logger emission failures to avoid feedback loops via console overrides.
  }
};

const sendToLogger = (level: LogLevel, args: unknown[]): void => {
  emitLogRecord(level, args, false);
};

// Public toggle for enabling/disabling remote log emission
export const setLogsEnabled = (enabled: boolean): void => {
  LOGS_ENABLED = !!enabled;
};

export const log = (...args: unknown[]): void => {
  sendToLogger("info", args);
};

export const warn = (...args: unknown[]): void => {
  sendToLogger("warn", args);
};

export const error = (...args: unknown[]): void => {
  sendToLogger("error", args);
};

// Force send logs regardless of LOGS_ENABLED flag (for errors and custom events)
export const forceLog = (...args: unknown[]): void => {
  emitLogRecord("info", args, true);
};

export const forceError = (...args: unknown[]): void => {
  emitLogRecord("error", args, true);
};

type ForceFlushOptions = {
  beacon?: boolean;
};

export const forceFlushLogs = async (options: ForceFlushOptions = {}): Promise<void> => {
  if (!loggerProcessor) {
    warn({
      message: "[L9RUM Browser SDK] Log processor unavailable for forceFlush",
      attributes: {
        "log.phase": "force_flush",
        "log.guard": "processor_missing",
      },
    });
    return;
  }
  if (options.beacon && loggerExporter) {
    loggerExporter.enterBeaconMode();
  }
  try {
    await loggerProcessor.forceFlush();
    log({
      message: "[L9RUM Browser SDK] Log processor flush completed",
      attributes: {
        "log.phase": "force_flush",
        "log.guard": options.beacon ? "beacon" : "standard",
      },
    });
  } catch (err) {
    error({
      message: "[L9RUM Browser SDK] Log processor flush failed",
      attributes: {
        "log.phase": "force_flush",
        "log.guard": options.beacon ? "beacon" : "standard",
      },
      error: err,
    });
    throw err;
  }
};
